import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage, getPlainError } from "api/errors";
import { useSettings } from "api/general";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate, useParams } from "react-router-dom";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import * as Yup from "yup";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    paper: {
      backgroundColor: theme.palette.background.paper,
      border: "none",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      maxWidth: "95%",
      padding: theme.spacing(12, 12, 16),
    },
    header: {
      color: theme.black,
      fontSize: 20,
      fontWeight: 500,
      lineHeight: "32px",
      marginBottom: theme.spacing(6),
    },
    grayTextSmall: {
      color: theme.gray500,
      display: "block",
      fontSize: 16,
      marginBottom: theme.spacing(12),
    },
    Input: {
      maxWidth: "100%",
      width: 406,
      "& + &": {
        marginTop: theme.spacing(10),
      },
    },
    submit: {
      height: theme.spacing(24),
      marginTop: theme.spacing(12),
    },
    form: {
      display: "flex",
      flexDirection: "column",
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      "& + &": { marginTop: 0 },
    },
  }),
);

export function PasswordReset() {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const { id: token } = useParams<{ id: string }>();
  const { resetPassword } = useSettings();
  const navigate = useNavigate();
  return (
    <>
      <div className={classes.paper}>
        <h1 className={classes.header}>{message({ id: "changePassword" })}</h1>
        <span className={classes.grayTextSmall}>
          {message({ id: "changePasswordInfo" })}
        </span>
        <Formik
          initialStatus={{}}
          initialValues={{ plainPassword: "", plainPassword2: "" }}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setSubmitting(true);
            setStatus({});
            try {
              await resetPassword(values.plainPassword, token as string);
              notify(message({ id: "passwordUpdated" }));
              navigate(links.homepage);
            } catch (exception) {
              setSubmitting(false);
              notify(
                getErrorMessage(exception) ||
                  (getPlainError(exception) as string),
              );
              const errors = backendErrors(getBackendErrors(exception));
              setStatus(errors);
              focusError();
            }
          }}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            plainPassword: Yup.string()
              .oneOf(
                [Yup.ref("plainPassword2"), undefined],
                message({ id: "passwordsNotEqualError" }),
              )
              .required(message({ id: "requiredFieldError" })),
            plainPassword2: Yup.string().required(
              message({ id: "requiredFieldError" }),
            ),
          })}
        >
          {({ handleSubmit, isSubmitting, status }) => {
            return (
              <Form onSubmit={handleSubmit} className={classes.form}>
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}
                <TextInput
                  className={classes.Input}
                  label={
                    <RequiredFieldLabel text={message({ id: "newPassword" })} />
                  }
                  name="plainPassword"
                  type="password"
                />
                <TextInput
                  className={classes.Input}
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "repeatNewPassword" })}
                    />
                  }
                  name="plainPassword2"
                  type="password"
                />
                <OLButton
                  className={classes.submit}
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                >
                  {message({ id: "changePassword" })}
                </OLButton>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
}
